import React, { useState, useEffect, useContext } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import { AuthContext } from '../../context/AuthContext';
import { useSidebarState } from '../../components/sidebar/SidebarStateContext';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Badge
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.scss';

// Configurar moment en español
moment.locale('es');
const localizer = momentLocalizer(moment);

const CalendarPage = () => {
  const { currentUser, currentService } = useContext(AuthContext);
  const { collapsed } = useSidebarState();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: new Date(),
    end: new Date(),
    type: 'ronda',
    location: '',
    description: '',
    vigilante: '',
    tienda: ''
  });

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "listContainer collapsed" : "listContainer";

  useEffect(() => {
    loadCalendarData();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, filterType]);

  const loadCalendarData = () => {
    try {
      // Importar los datos desde el archivo JSON
      import('../../dataset_teliman.json').then((data) => {
        const dataset = data.default || [];
        
        // Generar eventos basados en los datos del dataset
        const generatedEvents = generateEventsFromDataset(dataset);
        
        // Agregar eventos inventados para completar el calendario
        const inventedEvents = generateInventedEvents();
        
        const allEvents = [...generatedEvents, ...inventedEvents];
        
        setEvents(allEvents);
        setLoading(false);
      }).catch((error) => {
        console.error('Error loading calendar data:', error);
        // Si falla, usar solo eventos inventados
        setEvents(generateInventedEvents());
        setLoading(false);
      });
    } catch (error) {
      console.error('Error importing calendar data:', error);
      setEvents(generateInventedEvents());
      setLoading(false);
    }
  };

  const generateEventsFromDataset = (dataset) => {
    const events = [];
    
    dataset.forEach((item, index) => {
      if (item.fecha_registro) {
        // Crear evento de registro
        const startDate = new Date(item.fecha_registro);
        const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // 2 horas después
        
        events.push({
          id: `reg-${index}`,
          title: `Registro: ${item.usuario?.persona?.nombre || 'Usuario'} ${item.usuario?.persona?.apellido1 || ''}`,
          start: startDate,
          end: endDate,
          type: 'registro',
          location: item.usuario?.tiendas?.[0] || 'Tienda no especificada',
          description: `Registro de nuevo usuario en el sistema`,
          vigilante: `${item.usuario?.persona?.nombre || ''} ${item.usuario?.persona?.apellido1 || ''}`,
          tienda: item.usuario?.tiendas?.[0] || 'N/A'
        });
      }
    });
    
    return events;
  };

  const generateInventedEvents = () => {
    const inventedEvents = [];
    const today = new Date();
    
    // Tipos de eventos
    const eventTypes = [
      { type: 'ronda', title: 'Ronda de Seguridad', duration: 3, color: '#2e7d32' },
      { type: 'mantenimiento', title: 'Mantenimiento Preventivo', duration: 2, color: '#f57c00' },
      { type: 'reunion', title: 'Reunión de Coordinación', duration: 1, color: '#388e3c' },
      { type: 'capacitacion', title: 'Capacitación Básica', duration: 4, color: '#7b1fa2' },
      { type: 'auditoria', title: 'Auditoría Interna', duration: 6, color: '#d32f2f' },
      { type: 'emergencia', title: 'Simulacro de Emergencia', duration: 2, color: '#f44336' }
    ];
    
    const locations = [
      'Edificio Administrativo',
      'Planta de Producción',
      'Centro de Distribución',
      'Oficinas Regionales',
      'Almacén Central',
      'Sede Corporativa'
    ];
    
    const vigilantes = [
      'Carlos Ruiz',
      'María López',
      'José García',
      'Ana Martínez',
      'Pedro Sánchez',
      'Laura Fernández',
      'Miguel Torres',
      'Carmen Ruiz'
    ];
    
    // Generar eventos para los próximos 3 meses
    for (let i = 0; i < 90; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      
      // Generar 1-3 eventos por día
      const eventsPerDay = Math.floor(Math.random() * 3) + 1;
      
      for (let j = 0; j < eventsPerDay; j++) {
        const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
        const startHour = Math.floor(Math.random() * 12) + 6; // Entre 6 AM y 6 PM
        const startMinute = Math.floor(Math.random() * 4) * 15; // 0, 15, 30, 45
        
        const startDate = new Date(date);
        startDate.setHours(startHour, startMinute, 0, 0);
        
        const endDate = new Date(startDate);
        endDate.setHours(startDate.getHours() + eventType.duration);
        
        const location = locations[Math.floor(Math.random() * locations.length)];
        const vigilante = vigilantes[Math.floor(Math.random() * vigilantes.length)];
        
        inventedEvents.push({
          id: `inv-${i}-${j}`,
          title: `${eventType.title} - ${location}`,
          start: startDate,
          end: endDate,
          type: eventType.type,
          location: location,
          description: `Evento programado de ${eventType.title.toLowerCase()} en ${location}`,
          vigilante: vigilante,
          tienda: location,
          color: eventType.color
        });
      }
    }
    
    return inventedEvents;
  };

  const filterEvents = () => {
    if (filterType === 'all') {
      setFilteredEvents(events);
    } else {
      setFilteredEvents(events.filter(event => event.type === filterType));
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setOpenDialog(true);
  };

  const handleSelectSlot = (slotInfo) => {
    setNewEvent({
      ...newEvent,
      start: slotInfo.start,
      end: slotInfo.end
    });
    setSelectedEvent(null);
    setOpenDialog(true);
  };

  const handleSaveEvent = () => {
    if (selectedEvent) {
      // Editar evento existente
      setEvents(events.map(event => 
        event.id === selectedEvent.id ? { ...selectedEvent } : event
      ));
    } else {
      // Crear nuevo evento
      const event = {
        ...newEvent,
        id: `new-${Date.now()}`,
        color: getEventColor(newEvent.type)
      };
      setEvents([...events, event]);
    }
    setOpenDialog(false);
    setSelectedEvent(null);
    setNewEvent({
      title: '',
      start: new Date(),
      end: new Date(),
      type: 'ronda',
      location: '',
      description: '',
      vigilante: '',
      tienda: ''
    });
  };

  const handleDeleteEvent = () => {
    setEvents(events.filter(event => event.id !== selectedEvent.id));
    setOpenDialog(false);
    setSelectedEvent(null);
  };

  const getEventColor = (type) => {
    const colors = {
      ronda: '#2e7d32',
      mantenimiento: '#f57c00',
      reunion: '#388e3c',
      capacitacion: '#7b1fa2',
      auditoria: '#d32f2f',
      emergencia: '#f44336',
      registro: '#607d8b'
    };
    return colors[type] || '#757575';
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || getEventColor(event.type),
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const getEventsForDate = (date) => {
    return filteredEvents.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const todayEvents = getEventsForDate(new Date());

  return (
    <div className="list">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        
        <div className="calendar-content">
          <div className="page-header">
            <Typography variant="h4" component="h1" className="page-title">
              <CalendarIcon className="title-icon" />
              Calendario y Agenda
            </Typography>
            <Typography variant="body1" color="textSecondary" className="page-subtitle">
              Gestión de eventos, rondas y actividades programadas
            </Typography>
          </div>

          {/* Filtros y controles */}
          <Card className="controls-card">
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Evento</InputLabel>
                    <Select
                      value={filterType}
                      label="Tipo de Evento"
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="ronda">Rondas</MenuItem>
                      <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                      <MenuItem value="reunion">Reuniones</MenuItem>
                      <MenuItem value="capacitacion">Capacitación</MenuItem>
                      <MenuItem value="auditoria">Auditorías</MenuItem>
                      <MenuItem value="emergencia">Emergencias</MenuItem>
                      <MenuItem value="registro">Registros</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vista</InputLabel>
                    <Select
                      value={view}
                      label="Vista"
                      onChange={(e) => setView(e.target.value)}
                    >
                      <MenuItem value="month">Mes</MenuItem>
                      <MenuItem value="week">Semana</MenuItem>
                      <MenuItem value="day">Día</MenuItem>
                      <MenuItem value="agenda">Agenda</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      icon={<EventIcon />} 
                      label={`${filteredEvents.length} eventos`} 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      icon={<TodayIcon />} 
                      label={`${todayEvents.length} hoy`} 
                      color="secondary" 
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Calendario principal */}
            <Grid item xs={12} lg={8}>
              <Card className="calendar-card">
                <CardContent sx={{ padding: 0, height: '100%' }}>
                  <Box sx={{ height: '70vh', padding: '16px' }}>
                    <Calendar
                      localizer={localizer}
                      events={filteredEvents}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%' }}
                      view={view}
                      onView={setView}
                      onSelectEvent={handleSelectEvent}
                      onSelectSlot={handleSelectSlot}
                      selectable
                      eventPropGetter={eventStyleGetter}
                      messages={{
                        next: 'Siguiente',
                        previous: 'Anterior',
                        today: 'Hoy',
                        month: 'Mes',
                        week: 'Semana',
                        day: 'Día',
                        agenda: 'Agenda',
                        date: 'Fecha',
                        time: 'Hora',
                        event: 'Evento',
                        noEventsInRange: 'No hay eventos en este rango'
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Panel lateral */}
            <Grid item xs={12} lg={4}>
              <Grid container spacing={2}>
                {/* Eventos de hoy */}
                <Grid item xs={12}>
                  <Card className="today-events-card">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Eventos de Hoy
                      </Typography>
                      <List dense>
                        {todayEvents.slice(0, 5).map((event) => (
                          <ListItem key={event.id} button onClick={() => handleSelectEvent(event)}>
                            <ListItemIcon>
                              <Box 
                                sx={{ 
                                  width: 12, 
                                  height: 12, 
                                  borderRadius: '50%', 
                                  backgroundColor: event.color || getEventColor(event.type) 
                                }} 
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={event.title}
                              secondary={`${moment(event.start).format('HH:mm')} - ${moment(event.end).format('HH:mm')}`}
                            />
                          </ListItem>
                        ))}
                        {todayEvents.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No hay eventos programados para hoy" />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Resumen de eventos */}
                <Grid item xs={12}>
                  <Card className="summary-card">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Resumen del Mes
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon>
                            <EventIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Total de Eventos"
                            secondary={filteredEvents.length}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <PersonIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Vigilantes Activos"
                            secondary={new Set(filteredEvents.map(e => e.vigilante)).size}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <LocationIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Ubicaciones"
                            secondary={new Set(filteredEvents.map(e => e.location)).size}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Botón flotante para agregar evento */}
          <Fab
            color="primary"
            aria-label="add"
            className="add-event-fab"
            onClick={() => {
              setSelectedEvent(null);
              setOpenDialog(true);
            }}
          >
            <AddIcon />
          </Fab>

          {/* Dialog para crear/editar evento */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              {selectedEvent ? 'Editar Evento' : 'Nuevo Evento'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={selectedEvent?.title || newEvent.title}
                    onChange={(e) => {
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, title: e.target.value });
                      } else {
                        setNewEvent({ ...newEvent, title: e.target.value });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha y Hora Inicio"
                    type="datetime-local"
                    value={moment(selectedEvent?.start || newEvent.start).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, start: date });
                      } else {
                        setNewEvent({ ...newEvent, start: date });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Fecha y Hora Fin"
                    type="datetime-local"
                    value={moment(selectedEvent?.end || newEvent.end).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, end: date });
                      } else {
                        setNewEvent({ ...newEvent, end: date });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={selectedEvent?.type || newEvent.type}
                      label="Tipo"
                      onChange={(e) => {
                        if (selectedEvent) {
                          setSelectedEvent({ ...selectedEvent, type: e.target.value });
                        } else {
                          setNewEvent({ ...newEvent, type: e.target.value });
                        }
                      }}
                    >
                      <MenuItem value="ronda">Ronda</MenuItem>
                      <MenuItem value="mantenimiento">Mantenimiento</MenuItem>
                      <MenuItem value="reunion">Reunión</MenuItem>
                      <MenuItem value="capacitacion">Capacitación</MenuItem>
                      <MenuItem value="auditoria">Auditoría</MenuItem>
                      <MenuItem value="emergencia">Emergencia</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Vigilante"
                    value={selectedEvent?.vigilante || newEvent.vigilante}
                    onChange={(e) => {
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, vigilante: e.target.value });
                      } else {
                        setNewEvent({ ...newEvent, vigilante: e.target.value });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ubicación"
                    value={selectedEvent?.location || newEvent.location}
                    onChange={(e) => {
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, location: e.target.value });
                      } else {
                        setNewEvent({ ...newEvent, location: e.target.value });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    multiline
                    rows={3}
                    value={selectedEvent?.description || newEvent.description}
                    onChange={(e) => {
                      if (selectedEvent) {
                        setSelectedEvent({ ...selectedEvent, description: e.target.value });
                      } else {
                        setNewEvent({ ...newEvent, description: e.target.value });
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {selectedEvent && (
                <Button onClick={handleDeleteEvent} color="error">
                  <DeleteIcon /> Eliminar
                </Button>
              )}
              <Button onClick={() => setOpenDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEvent} variant="contained">
                {selectedEvent ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;


